import { Fragment, type ReactNode } from 'react';
import { trechosDoTermo } from './busca-na-conversa';

/**
 * Renderiza texto no estilo WhatsApp:
 * - `*texto*` → **negrito**
 * - `_texto_` → *itálico*
 * - `~texto~` → ~~riscado~~
 * - `` `texto` `` → `mono`
 * - URLs (http://, https://, www.) viram <a> clicáveis (target=_blank, noopener)
 * - quebras de linha (\n) viram <br/>
 * - `termo` (busca na conversa): cada ocorrência vira <mark>, sem diferença de
 *   acento nem de maiúscula. Sem termo, a saída é a de sempre.
 *
 * Sem dangerouslySetInnerHTML — constrói árvore de elementos React (XSS-safe).
 *
 * Limitações conscientes (mesmo padrão do WhatsApp oficial):
 * - Marcadores não atravessam linhas (ex: `*neg\nrito*` não vira negrito).
 * - Sem aninhamento (`*a _b_ c*` aplica só negrito; itálico interno fica literal).
 *   Pra MVP é suficiente — a esmagadora maioria das mensagens usa só 1 nível.
 */
export function renderWhatsAppText(text: string, termo?: string): ReactNode {
  const lines = text.split('\n');
  return lines.map((line, lineIdx) => (
    // Index é estável porque o input é imutável dentro de uma render.
    // biome-ignore lint/suspicious/noArrayIndexKey: linhas são posicionais
    <Fragment key={lineIdx}>
      {lineIdx > 0 && <br />}
      {parseInline(line, termo)}
    </Fragment>
  ));
}

/**
 * Amarelo com texto escuro forçado: legível nas duas bolhas e nos dois temas
 * (a recebida no escuro tem texto claro, que sumiria no amarelo).
 */
const MARCA_DA_BUSCA = 'rounded-[2px] bg-[#fde68a] text-[#09090B]';

/**
 * Destaca o termo num pedaço de texto puro. Um termo que atravessa a fronteira
 * de uma formatação (`*bol*eto`) não é destacado — a bolha continua achada
 * (e com o anel do resultado atual), só sem o amarelo.
 */
function destacar(texto: string, termo: string | undefined, chave: string): ReactNode {
  if (!termo) return texto;
  const trechos = trechosDoTermo(texto, termo);
  if (trechos.length === 0) return texto;
  const out: ReactNode[] = [];
  let de = 0;
  trechos.forEach(([a, b], i) => {
    if (a > de) out.push(texto.slice(de, a));
    out.push(
      // biome-ignore lint/suspicious/noArrayIndexKey: posição estável dentro do parse
      <mark key={`${chave}-${i}`} className={MARCA_DA_BUSCA}>
        {texto.slice(a, b)}
      </mark>,
    );
    de = b;
  });
  if (de < texto.length) out.push(texto.slice(de));
  return <Fragment key={chave}>{out}</Fragment>;
}

// Captura cada marcador conhecido em grupos. Conteúdo entre marcadores não pode
// ter newline nem o próprio caractere de fechamento.
const INLINE_REGEX = /\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~|`([^`\n]+)`/g;

// URL pattern conservador: http(s):// completo OU www. seguido de domain. Pega
// até primeiro whitespace ou char "claramente terminador" (espaço, vírgula
// seguida de espaço, ponto final no fim, etc — não tentamos cobrir 100% de
// edge cases, melhor false negative que sublinhar parte de uma frase). Pontuação
// final comum (., ,, !, ?, ;, ), ]) é trimada DEPOIS via cleanup. RFC 3986 não
// limita esses chars em URL, mas a esmagadora maioria das URLs reais não termina
// neles — vale o trade-off de UX.
const URL_REGEX = /(https?:\/\/[^\s<>"'`]+|www\.[^\s<>"'`]+)/g;

function parseInline(text: string, termo?: string): ReactNode[] {
  const out: ReactNode[] = [];
  let lastIdx = 0;
  let key = 0;

  const pushText = (str: string): void => {
    if (!str) return;
    out.push(...linkifyText(str, key, termo));
    key += 1000; // espaço pras subkeys de links não colidirem
  };

  for (const match of text.matchAll(INLINE_REGEX)) {
    const start = match.index;
    if (start > lastIdx) pushText(text.slice(lastIdx, start));

    if (match[1] !== undefined) {
      // biome-ignore lint/suspicious/noArrayIndexKey: posição estável dentro do parse
      out.push(<strong key={`b-${key}`}>{destacar(match[1], termo, `b-${key++}`)}</strong>);
    } else if (match[2] !== undefined) {
      // biome-ignore lint/suspicious/noArrayIndexKey: idem
      out.push(<em key={`i-${key}`}>{destacar(match[2], termo, `i-${key++}`)}</em>);
    } else if (match[3] !== undefined) {
      // biome-ignore lint/suspicious/noArrayIndexKey: idem
      out.push(<s key={`s-${key}`}>{destacar(match[3], termo, `s-${key++}`)}</s>);
    } else if (match[4] !== undefined) {
      out.push(
        // biome-ignore lint/suspicious/noArrayIndexKey: idem
        <code key={`c-${key}`} className="rounded bg-black/10 px-1 font-mono text-[0.92em]">
          {destacar(match[4], termo, `c-${key++}`)}
        </code>,
      );
    }

    lastIdx = start + match[0].length;
  }

  if (lastIdx < text.length) pushText(text.slice(lastIdx));
  return out.length > 0 ? out : [destacar(text, termo, 'l')];
}

/**
 * Detecta URLs em texto puro e quebra em string + <a>.
 * Pontuação final colada (.,!?;)) é deslocada pra FORA do link
 * (regex come até primeiro espaço, então frases tipo "veja https://x.com."
 * teriam o "." virando parte da URL).
 */
function linkifyText(text: string, baseKey: number, termo?: string): ReactNode[] {
  const out: ReactNode[] = [];
  let lastIdx = 0;
  let i = 0;

  for (const match of text.matchAll(URL_REGEX)) {
    const start = match.index;
    if (start > lastIdx) out.push(destacar(text.slice(lastIdx, start), termo, `t-${baseKey}-${i}`));

    let url = match[0] ?? '';
    // Trim pontuação final que provavelmente NÃO é parte da URL.
    let trailing = '';
    while (url.length > 0 && /[.,!?;:)\]}"']/.test(url[url.length - 1] ?? '')) {
      trailing = (url[url.length - 1] ?? '') + trailing;
      url = url.slice(0, -1);
    }
    const href = url.startsWith('www.') ? `https://${url}` : url;

    out.push(
      <a
        // biome-ignore lint/suspicious/noArrayIndexKey: chave estável dentro do parse
        key={`url-${baseKey}-${i++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="break-all underline decoration-current/40 hover:decoration-current"
      >
        {destacar(url, termo, `u-${baseKey}-${i}`)}
      </a>,
    );
    if (trailing) out.push(trailing);

    lastIdx = start + (match[0] ?? '').length;
  }

  if (lastIdx < text.length) out.push(destacar(text.slice(lastIdx), termo, `t-${baseKey}-fim`));
  return out.length > 0 ? out : [destacar(text, termo, `t-${baseKey}`)];
}
